import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useNavigate, type NavLinkProps } from 'react-router';
import { useAccess } from '@lumlab/platform-admin/web';
import { Badge, Button, Modal, ThemeToggle, useDisclosure } from '@lumlab/platform-ui';
import { products } from '../products.ts';
import { authClient } from './auth.ts';
import { BrandLogo } from './BrandLogo.tsx';

function NavigationLink(props: Omit<NavLinkProps, 'className'>) {
    return (
        <NavLink
            {...props}
            className={({ isActive }) =>
                `relative block rounded-lg px-3 py-2 text-sm transition-colors before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-full ${isActive ? 'bg-surface-muted font-semibold text-ink before:bg-brand' : 'text-muted before:bg-transparent hover:bg-surface-muted hover:text-ink'}`
            }
        />
    );
}

export function AppLayout({ administration = false }: { administration?: boolean }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { opened, open, close } = useDisclosure();
    const session = authClient.useSession();
    const access = useAccess(session.data?.user.id);
    const organization = authClient.useActiveOrganization();

    const signOut = async () => {
        await authClient.signOut();
        queryClient.clear();
        await navigate('/sign-in', { replace: true });
    };
    const navigation = (
        <nav aria-label={t('platform.shell.navigation')} className="space-y-1">
            {administration ? (
                <>
                    <NavigationLink to="/admin/users" onClick={close}>
                        {t('platform.admin.users')}
                    </NavigationLink>
                    <NavigationLink to="/admin/organizations" onClick={close}>
                        {t('platform.admin.organizations')}
                    </NavigationLink>
                    <NavigationLink to="/admin/products" end onClick={close}>
                        {t('platform.admin.products')}
                    </NavigationLink>
                    {products
                        .filter((product) => product.administration)
                        .map((product) => (
                            <NavigationLink key={product.id} to={`/admin/products/${product.id}`} onClick={close}>
                                {t(`${product.id}.administration.title`)}
                            </NavigationLink>
                        ))}
                </>
            ) : (
                <>
                    <NavigationLink to="/" end onClick={close}>
                        {t('platform.shell.home')}
                    </NavigationLink>
                    {access.data?.administrator && (
                        <NavigationLink to="/admin" onClick={close}>
                            {t('platform.admin.title')}
                        </NavigationLink>
                    )}
                    {products
                        .filter((product) => access.data?.products.includes(product.id))
                        .map((product) => (
                            <div key={product.id} className="space-y-1 pt-4">
                                <p className="px-3 pb-1 text-xs font-semibold tracking-wide text-muted uppercase">
                                    {t(`${product.id}.product.name`)}
                                </p>
                                {product.navigation.map((item) => (
                                    <NavigationLink key={item.path} to={item.path} onClick={close}>
                                        {t(item.labelKey)}
                                    </NavigationLink>
                                ))}
                            </div>
                        ))}
                </>
            )}
        </nav>
    );

    return (
        <div className="min-h-dvh">
            <header className="fixed inset-x-0 top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-line bg-surface px-4">
                <div className="flex min-w-0 items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="sm:hidden"
                        onClick={open}
                        aria-expanded={opened}
                        aria-haspopup="dialog"
                        aria-label={t('platform.shell.navigation')}
                    >
                        <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            className="size-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </Button>
                    <Link to={administration ? '/admin' : '/'} className="flex shrink-0 rounded-md">
                        {/* The symbol alone keeps the header within 320 px (WCAG reflow). */}
                        <BrandLogo layout="symbol" className="size-8 sm:hidden" />
                        <span className="hidden sm:contents">
                            <BrandLogo className="h-8 w-auto" />
                        </span>
                    </Link>
                    {administration && (
                        <span className="hidden sm:inline-flex">
                            <Badge tone="warning">{t('platform.admin.title')}</Badge>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="hidden text-right leading-tight md:block">
                        <p className="text-sm font-semibold">{organization.data?.name}</p>
                        <p className="text-xs text-muted">{session.data?.user.email}</p>
                    </div>
                    <ThemeToggle />
                    <Button variant="default" size="xs" onClick={() => void signOut()}>
                        {t('platform.auth.signOut')}
                    </Button>
                </div>
            </header>
            <aside className="fixed top-16 bottom-0 left-0 hidden w-64 overflow-y-auto border-r border-line bg-surface p-3 sm:block">
                {navigation}
            </aside>
            <Modal opened={opened} onClose={close} title={t('platform.shell.navigation')}>
                {navigation}
            </Modal>
            <main className="min-w-0 px-4 pt-24 pb-10 sm:ml-64 sm:px-8">
                <div className="mx-auto flex max-w-6xl flex-col gap-4">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
